import React, { useCallback, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useStableValue } from '@/hooks/use-shallow-stable-value'
import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import type { LeftPanelRef } from '@/components/app/panels/left-panel'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { samePanelParagraphs } from '../../helpers/same-panel-paragraphs'
import { nextNewTextBoxStyle } from '../../helpers/next-new-text-box-style'
import type { NewTextBoxStyle } from '../../types/new-text-box-style'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { useQueryClient } from '@tanstack/react-query'
import { useHealthCheck } from '@/api/hooks/use-health'
import { useReadinessCheck } from '@/api/hooks/use-readiness'
import { useAuth } from '@/providers/auth'
import type { ChainSeamMemo } from '@/lib/text-linking/chain-story'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { FontFamily } from '@/api/types/font-family'
import type { Paragraph, TextChain } from '@/api/types/stowed-element'
import type { ShapeTextBakeContext } from '@/lib/elements/stowed-element-builder'
import type { ChainStyleApplyResult } from '../../types/chain-style-apply-result'
import { NOT_HANDLED_CHAIN_STYLE } from '../../constants/chain-style'

/**
 * What the rest of the editor screen builds on: the query client, auth and navigation, the
 * ShapeManager / canvas / left-panel refs, the font refs the font bridge fills, the text-style
 * panel's handlers and last-used new-text style, the linked-text chain state and refs, the unified
 * layer order, and the health/readiness queries.
 */
export function useHomeFoundation() {
  const queryClient = useQueryClient()
  const { token, username, logout } = useAuth()
  const navigate = useNavigate()
  const [, setMenuOpen] = React.useState(false)

  const [, setCurrentSpreadIndex] = React.useState(0)
  const shapeManagerRef = useRef<ShapeManagerRef | null>(null)
  /**
   * Resolves the shape-text bake context every `buildStowedShapeElement` call attaches — see that
   * function. Declared here, next to the manager it reads, but ASSIGNED during render by
   * `useHomeTextChains`; the closure only runs from event handlers, long after both are in scope.
   *
   * A ref rather than a `useCallback` so it stays out of ~15 dependency arrays: its identity must not
   * change, and it reads nothing that isn't already a ref.
   */
  const shapeTextBakeRef = useRef<(shape: ShapeData) => ShapeTextBakeContext>(() => ({}))
  const centerCanvasRef = useRef<CenterCanvasHandle | null>(null)
  const leftPanelRef = useRef<LeftPanelRef | null>(null)
  /** Corner-handle resize gesture in progress (shape or text) — object panel briefly shows "linked" dimensions. */
  const [isCornerResizeActive, setIsCornerResizeActive] = useState(false)
  /** Synced by `TextChainFontsBridge` (mounted inside `<FontProvider>`) for use in `buildChainStoryFrames`. */
  const fontLookupRef = useRef<ParagraphBlockFontLookup | null>(null)
  /** Synced by `TextChainFontsBridge` for use in `handleAddCaption` (resolving the default caption font). */
  const fontFamiliesRef = useRef<FontFamily[]>([])

  // Linked-text chains — wire-shaped (uid strings) source of truth for text linking (WMPCM-6852).
  // Reset on spread switch, then hydrated once the new spread's checkpoint metadata arrives.
  // Declared here (ahead of the text-box persistence handlers section below, where this
  // conceptually belongs) because handleShapeCommitted's group-transform branch also needs to
  // trigger a chain reflow for any chain-linked text box resized as part of a group.
  const [textChains, setTextChains] = useState<TextChain[]>([])
  /**
   * Live mirror of `textChains`, refreshed every render *and* advanced imperatively by
   * `repairChainAfterFrameDeleted` before its `setTextChains` lands.
   *
   * Deleting several frames at once fires `onShapeDeleted`/`onTextBoxDeleted` once per frame,
   * synchronously, inside a single React batch — no render happens in between, so every repair in
   * the gesture would otherwise read the same pre-delete `textChains` closure value and the last
   * write would win, leaving a chain that still lists frames removed earlier in the same gesture
   * (WMPCM-6988 TC-05). Reading through this ref makes each repair see its predecessors' result.
   */
  const textChainsRef = useRef(textChains)

  textChainsRef.current = textChains

  /**
   * The ids a single delete/cut gesture is removing, announced by CenterCanvas *before* the
   * managers start removing them — see `onDeleteGestureScope` there. Consumed by txnId match only,
   * so a scope left over from an earlier gesture can never be mistaken for the current one and
   * there is no clearing lifecycle to get wrong.
   */
  const deleteGestureScopeRef = useRef<{ txnId: string; ids: Set<string> } | null>(null)
  /**
   * Which chains a delete/cut gesture has already redistributed, so the remaining per-frame repairs
   * skip the CONTENT pass while still doing their topology work.
   *
   * `repairChainAfterFrameDeleted`'s first call for a chain builds the story from every one of its
   * frames — the dying ones included, since they are still readable — and re-fills the survivors with
   * all of it. By the time a second victim's repair runs, the survivors already hold that text while
   * the remaining victims still hold their original slices, so re-assembling would count the same
   * content twice.
   *
   * Keyed on the CHAIN, not the gesture: one gesture can cut members of two independent chains, and
   * each of those chains needs its own single redistribution. `gestureKey` scopes the set so it
   * resets on the next gesture without a clearing lifecycle to get wrong — same reasoning as
   * `deleteGestureScopeRef` above.
   */
  const chainStoryRedistributedRef = useRef<{ gestureKey: string; chainIds: Set<string> }>({
    gestureKey: '',
    chainIds: new Set(),
  })
  /**
   * What each distribution decided about each chain boundary, so the next assembly does not have to
   * re-derive it — see `ChainSeamMemo`.
   *
   * Every `storyFromChain` call in home's chain hooks reads it and every distribution records into it. Without
   * it `seamWasCharacterSplit` runs against a frame that is over capacity (that is what provoked the
   * reflow) and rejects every seam, so a paragraph flowing across a link comes back apart into two.
   *
   * Cleared on spread change: entries are keyed by frame id, and nothing on the next spread should
   * inherit an answer about a boundary on this one.
   */
  const chainSeamMemoRef = useRef<ChainSeamMemo>(new Map())
  /**
   * Each chain member's paragraphs as they stood before `projectChainStoryLive` first rewrote them in
   * the current edit window, keyed by frame id.
   *
   * The live projection flows the chain on every keystroke but writes no history and enqueues
   * nothing — persistence and undo stay on the 500 ms debounce. That leaves the debounced
   * `redistributeChainStory` looking at frames it has already moved: `changedSlices` would report
   * every downstream frame unchanged, so the frame that received the flowed text would get **no
   * history action and never be enqueued at all**. Reload would then lose it and the upstream frame
   * would show its overset again.
   *
   * So the commit compares against, and records `before` from, the entry here rather than the live
   * model. Entries are recorded once per frame per window (first projection wins) and dropped by the
   * commit that consumes them, so the next window's `before` is this window's committed state and
   * never two windows deep.
   *
   * **The frame being edited is never in here** — the keystroke has already landed in it, so the
   * manager's `editStartShapeTextParagraphsRef` is the only baseline that predates the edit. See the
   * write loop in `projectChainStoryLive`.
   *
   * Cleared on spread change with the seam memo, for the same reason: frame ids do not survive it.
   */
  const chainProjectionBaselineRef = useRef<Map<string, Paragraph[]>>(new Map())

  // Unified bottom-to-top z-order for all drawable elements.
  const [unifiedLayerIds, setUnifiedLayerIds] = useState<string[]>([])
  // Ref mirrors the state so handleUnifiedLayerArrange can read current order synchronously
  // without needing to close over the state value (which would be stale in a useCallback).
  const unifiedLayerIdsRef = useRef<string[]>([])

  const [selectedTextBoxPanelIds, setSelectedTextBoxPanelIds] = React.useState<string[]>([])
  /** Bump-only counter so in-place text manager updates re-render the type panel. */
  const [textPanelRevision, bumpTextPanelRevision] = React.useState(0)
  /**
   * Bumped on shape text edit enter/exit (`EditorCommandState.shapeTextEditRevision`). The Type
   * panel re-syncs from it: its edit-state getters read live state and never change identity.
   */
  const [shapeTextEditRevision, setShapeTextEditRevision] = React.useState(0)
  /**
   * The few text settings a new text box picks up from the last TypePanel change: kerning,
   * tracking, underline and alignment only (`nextNewTextBoxStyle`). Font family, font and point
   * size are never carried over — a new text box always starts in the book/checkpoint default font
   * and size, so changing one frame's font never changes the next frame's (WMPCM-9964; see the
   * text rules). Leading is not carried over either — new text always starts at Metric leading.
   */
  const [activeNewTextBoxStyle, setActiveNewTextBoxStyle] = React.useState<NewTextBoxStyle>({})

  // The getter builds a fresh array on every call (and fresh default paragraphs for a shape with no
  // text yet); keep the previous one while it holds the same paragraphs, so the memoized left panel
  // doesn't re-render on every home render.
  const selectedShapeTextParagraphsForPanel = useStableValue(
    shapeManagerRef.current?.getSelectedShapeTextParagraphs() ?? null,
    samePanelParagraphs
  )
  const isSelectedShapeTextFrameForPanel =
    shapeManagerRef.current?.getSelectedShapeIsTextFrame() ?? false

  // WMPCM-6924: chain-aware whole-box style apply. Held in a ref because the implementation
  // (`applyTextStyleAcrossChain`) is declared far below — after redistributeChainStory — yet is
  // called from the stable handleTextStyleUpdate callback here. Returns true when it handled a
  // chained selection; `handled: false` means fall back to the normal per-object apply.
  const applyTextStyleAcrossChainRef = useRef<(u: TextRunStyleUpdate) => ChainStyleApplyResult>(
    () => NOT_HANDLED_CHAIN_STYLE
  )

  const handleTextStyleUpdate = useCallback((u: TextRunStyleUpdate) => {
    setActiveNewTextBoxStyle((prev) => nextNewTextBoxStyle(prev, u))

    // Shape text edit mode – highest priority; skip text-box routing entirely
    if (shapeManagerRef.current?.getEditingShapeTextId()) {
      shapeManagerRef.current.applyShapeTextRunStyle(u)
      return
    }

    // Select mode: apply to both shapes and text boxes (each is a no-op if nothing qualifies).
    //
    // The chain apply runs FIRST and reports which shapes it already styled, so `applyShapeTextRunStyle`
    // can skip them. Both paths reach a selected shape that is also a chain member, and styling it
    // twice is not harmless: `fontIdByFontId` is a per-run *remapping*, not an assignment, so a second
    // pass can land on a different variant than the first, and two commits with two historyTxnIds make
    // undo take two presses and leave the chain half-styled after the first (WMPCM-9070). They only
    // failed to overlap before because the chain path dropped shapes entirely.
    const chainStyle = applyTextStyleAcrossChainRef.current(u)

    shapeManagerRef.current?.applyShapeTextRunStyle(u, chainStyle.claimedShapeIds)
  }, [])

  // Kerning is caret-only — it never applies outside active editing, and (unlike Tracking/
  // Leading) never seeds a "next new text box" default, since a fresh text box always starts
  // at Natural (0) regardless of the last kerning value used.
  const handleKerningAtCaret = useCallback((value: number): boolean => {
    if (shapeManagerRef.current?.getEditingShapeTextId()) {
      return shapeManagerRef.current.applyShapeTextKerningAtCaret(value)
    }

    return false
  }, [])

  const handleTextRunStyleAppliedForPanel = useCallback(() => {
    bumpTextPanelRevision((n) => n + 1)
  }, [])
  /** Left edge of the right panel icon rail — zoom preset menu aligns its right edge here. */
  const zoomDropdownAlignRef = useRef<HTMLDivElement | null>(null)
  /** Slot under the spread area that the right panel stacks into on narrow viewports. */
  const rightPanelStackSlotRef = useRef<HTMLDivElement | null>(null)

  // Health check query
  useHealthCheck()

  // Readiness check query
  useReadinessCheck()

  return {
    token,
    username,
    logout,
    setMenuOpen,
    setCurrentSpreadIndex,
    isCornerResizeActive,
    setIsCornerResizeActive,
    textChains,
    setTextChains,
    unifiedLayerIds,
    setUnifiedLayerIds,
    selectedTextBoxPanelIds,
    setSelectedTextBoxPanelIds,
    textPanelRevision,
    bumpTextPanelRevision,
    shapeTextEditRevision,
    setShapeTextEditRevision,
    activeNewTextBoxStyle,
    setActiveNewTextBoxStyle,
    queryClient,
    navigate,
    shapeManagerRef,
    shapeTextBakeRef,
    centerCanvasRef,
    leftPanelRef,
    fontLookupRef,
    fontFamiliesRef,
    textChainsRef,
    deleteGestureScopeRef,
    chainStoryRedistributedRef,
    chainSeamMemoRef,
    chainProjectionBaselineRef,
    unifiedLayerIdsRef,
    selectedShapeTextParagraphsForPanel,
    isSelectedShapeTextFrameForPanel,
    applyTextStyleAcrossChainRef,
    handleTextStyleUpdate,
    handleKerningAtCaret,
    handleTextRunStyleAppliedForPanel,
    zoomDropdownAlignRef,
    rightPanelStackSlotRef,
  }
}
